"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { countSyllables } from "@/lib/tool-kit/style-checkers/syllables";

const RE_SPLIT_WORDS = /\s+/;

const WPM_SILENT = 238;
const WPM_SPEAKING = 130;
const WPM_PRESENTATION = 110;

type Strings = {
  [K in keyof typeof READING_TIME_CALCULATOR_STRINGS]: string;
};

function formatTime(minutes: number, strings: Strings): string {
  if (minutes < 1) {
    return t(strings.timeSeconds, { seconds: Math.ceil(minutes * 60) });
  }
  const m = Math.floor(minutes);
  const s = Math.round((minutes - m) * 60);
  if (s === 0) {
    return t(strings.timeMinutes, { minutes: m });
  }
  return t(strings.timeMinutesSeconds, { minutes: m, seconds: s });
}

interface ReadingStats {
  words: number;
  silentTime: string;
  speakingTime: string;
  presentationTime: string;
  complexWordPct: number;
  complexityLabel: string;
  silentMinutes: number;
  speakingMinutes: number;
  presentationMinutes: number;
}

function computeReadingStats(text: string, strings: Strings): ReadingStats {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return {
      words: 0,
      silentTime: t(strings.timeSeconds, { seconds: 0 }),
      speakingTime: t(strings.timeSeconds, { seconds: 0 }),
      presentationTime: t(strings.timeSeconds, { seconds: 0 }),
      complexWordPct: 0,
      complexityLabel: "—",
      silentMinutes: 0,
      speakingMinutes: 0,
      presentationMinutes: 0,
    };
  }
  const wordsArray = trimmed.split(RE_SPLIT_WORDS).filter(Boolean);
  const words = wordsArray.length;
  const complexWords = wordsArray.filter((w) => countSyllables(w) >= 3).length;
  const complexWordPct =
    words > 0 ? Math.round((complexWords / words) * 100) : 0;

  let complexityLabel = strings.complexitySimple;
  if (complexWordPct >= 30) {
    complexityLabel = strings.complexityVeryComplex;
  } else if (complexWordPct >= 20) {
    complexityLabel = strings.complexityComplex;
  } else if (complexWordPct >= 10) {
    complexityLabel = strings.complexityModerate;
  }

  const silentMinutes = words / WPM_SILENT;
  const speakingMinutes = words / WPM_SPEAKING;
  const presentationMinutes = words / WPM_PRESENTATION;

  return {
    words,
    silentTime: formatTime(silentMinutes, strings),
    speakingTime: formatTime(speakingMinutes, strings),
    presentationTime: formatTime(presentationMinutes, strings),
    complexWordPct,
    complexityLabel,
    silentMinutes,
    speakingMinutes,
    presentationMinutes,
  };
}

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      {sub && <p className="text-muted-foreground text-xs">{sub}</p>}
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const READING_TIME_CALCULATOR_STRINGS = {
  textToCalculateReadingTime: "Text to calculate reading time for",
  pasteOrTypeYourText: "Paste or type your text here...",
  words: "Words",
  silentReading: "Silent Reading",
  speakingAloud: "Speaking Aloud",
  presentation: "Presentation",
  wordComplexity: "Word Complexity",
  complexWords3Syllables: "Complex Words (3+ syllables)",
  vocabularyComplexity: "Vocabulary Complexity",
  commonLengthTargets: "Common Length Targets",
  targetWordCount: "one:Target: ~{count} word|other:Target: ~{count} words",
  timeSeconds: "{seconds} sec",
  timeMinutes: "{minutes} min",
  timeMinutesSeconds: "{minutes} min {seconds} sec",
  complexitySimple: "Simple",
  complexityModerate: "Moderate",
  complexityComplex: "Complex",
  complexityVeryComplex: "Very Complex",
  wpmSub: "@ {wpm} wpm",
  targetBlogPost: "Blog post (7 min read)",
  targetSpeech: "5-min speech",
  targetPresentation: "10-min presentation",
  targetKeynote: "20-min keynote",
  targetPodcast: "30-min podcast episode",
  targetElevatorPitch: "1-min elevator pitch",
  wordsOver: "one: {count} word over|other: {count} words over",
  wordsToGo: "one: {count} word to go|other: {count} words to go",
} as const;

export default function ReadingTimeCalculatorWidget() {
  const [text, setText] = useState("");

  const strings = useToolUi(READING_TIME_CALCULATOR_STRINGS);
  const stats = computeReadingStats(text, strings);
  const locale = useToolLocale();

  const fmt = useToolNumber();
  const formatNum = useToolNumber();

  return (
    <>
      <textarea
        aria-label={strings.textToCalculateReadingTime}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.pasteOrTypeYourText}
        value={text}
      />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label={strings.words} value={fmt(stats.words)} />
        <StatCard
          label={strings.silentReading}
          sub={t(strings.wpmSub, { wpm: WPM_SILENT })}
          value={stats.silentTime}
        />
        <StatCard
          label={strings.speakingAloud}
          sub={t(strings.wpmSub, { wpm: WPM_SPEAKING })}
          value={stats.speakingTime}
        />
        <StatCard
          label={strings.presentation}
          sub={t(strings.wpmSub, { wpm: WPM_PRESENTATION })}
          value={stats.presentationTime}
        />
      </div>
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.wordComplexity}
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg border border-border bg-card p-4 text-center">
            <p className="font-semibold text-2xl text-foreground">
              {stats.complexWordPct}%
            </p>
            <p className="mt-1 text-muted-foreground text-sm">
              {strings.complexWords3Syllables}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 text-center">
            <p className="font-semibold text-2xl text-foreground">
              {stats.complexityLabel}
            </p>
            <p className="mt-1 text-muted-foreground text-sm">
              {strings.vocabularyComplexity}
            </p>
          </div>
        </div>
      </section>
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.commonLengthTargets}
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {[
            {
              label: strings.targetBlogPost,
              wpm: WPM_SILENT,
              targetMins: 7,
            },
            { label: strings.targetSpeech, wpm: WPM_SPEAKING, targetMins: 5 },
            {
              label: strings.targetPresentation,
              wpm: WPM_PRESENTATION,
              targetMins: 10,
            },
            {
              label: strings.targetKeynote,
              wpm: WPM_PRESENTATION,
              targetMins: 20,
            },
            {
              label: strings.targetPodcast,
              wpm: WPM_SPEAKING,
              targetMins: 30,
            },
            {
              label: strings.targetElevatorPitch,
              wpm: WPM_SPEAKING,
              targetMins: 1,
            },
          ].map(({ label, wpm, targetMins }) => {
            const targetWords = Math.round(wpm * targetMins);
            const diff = stats.words - targetWords;
            const over = diff > 0;
            return (
              <div
                className="rounded-lg border border-border bg-card p-3"
                key={label}
              >
                <p className="font-medium text-foreground text-sm">{label}</p>
                <p className="mt-1 text-muted-foreground text-xs">
                  {tPlural(strings.targetWordCount, locale, targetWords, {
                    count: formatNum(targetWords),
                  })}
                </p>
                {stats.words > 0 && (
                  <p
                    className={`text-xs ${over ? "text-destructive" : "text-muted-foreground"}`}
                  >
                    {over
                      ? tPlural(strings.wordsOver, locale, diff, {
                          count: formatNum(diff),
                        })
                      : tPlural(strings.wordsToGo, locale, Math.abs(diff), {
                          count: formatNum(Math.abs(diff)),
                        })}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
